import { useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

const SESSION_STORAGE_KEYS = {
  lastActivity: 'returnify_last_activity',
  sessionStart: 'returnify_session_start',
  userRole: 'returnify_user_role',
};

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize session tracking on login
  const initializeSessionTracking = useCallback((role: string = 'user') => {
    const now = Date.now().toString();
    
    if (!localStorage.getItem(SESSION_STORAGE_KEYS.sessionStart)) {
      localStorage.setItem(SESSION_STORAGE_KEYS.sessionStart, now);
    }
    
    localStorage.setItem(SESSION_STORAGE_KEYS.lastActivity, now);
    localStorage.setItem(SESSION_STORAGE_KEYS.userRole, role);
  }, []);

  // Clear session tracking on logout
  const clearSessionTracking = useCallback(() => {
    localStorage.removeItem(SESSION_STORAGE_KEYS.lastActivity);
    localStorage.removeItem(SESSION_STORAGE_KEYS.sessionStart);
    localStorage.removeItem(SESSION_STORAGE_KEYS.userRole);
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST (before checking session)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Handle session events
        if (event === 'SIGNED_IN' && session) {
          // Initialize session tracking
          initializeSessionTracking('user');
        } else if (event === 'SIGNED_OUT') {
          clearSessionTracking();
        } else if (event === 'TOKEN_REFRESHED') {
          // Update activity on token refresh
          localStorage.setItem(SESSION_STORAGE_KEYS.lastActivity, Date.now().toString());
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // If session exists, ensure tracking is initialized
      if (session) {
        const existingStart = localStorage.getItem(SESSION_STORAGE_KEYS.sessionStart);
        if (!existingStart) {
          initializeSessionTracking('user');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [initializeSessionTracking, clearSessionTracking]);

  // hCaptcha temporarily disabled
  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    
    if (error) {
      throw error;
    }
    
    // Initialize session tracking for new user
    initializeSessionTracking('user');
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      throw error;
    }
    
    // Session tracking is handled by onAuthStateChange
  };

  const signOut = async () => {
    clearSessionTracking();
    
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
    navigate('/');
  };

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    initializeSessionTracking,
    clearSessionTracking,
  };
};
